import React, { createContext, useContext, useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const CustomThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(CustomThemeContext);
  if (!context) {
    throw new Error("useTheme debe ser usado dentro de ThemeContextEditor");
  }
  return context;
};

export const ThemeContextEditor = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme-preference");
    if (savedTheme) {
      setIsDarkMode(savedTheme === "light");
    } else {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: light)"
      ).matches;
      setIsDarkMode(prefersDark);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("theme-preference", isDarkMode ? "light" : "dark");
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const themeColors = {
    dark: {
      background: {
        primary: "#0a0a0a",
        secondary: "#111111",
        tertiary: "#151515",
        card: "#111111",
        cardHover: "#151515",
        paper: "#111111",
        drawer: "#0f0f0f",
      },
      text: {
        primary: "#ffffff",
        secondary: "#b3b3b3",
        tertiary: "#888888",
        muted: "#666666",
      },
      border: {
        primary: "#1a1a1a",
        secondary: "#333333",
        tertiary: "#404040",
      },
      accent: {
        primary: "rgba(120, 119, 198, 0.03)",
        secondary: "rgba(120, 119, 198, 0.02)",
      },
      button: {
        outlined: {
          border: "#333333",
          text: "#ffffff",
          hover: "#151515",
        },
        contained: {
          background: "#0F52BA",
          text: "#ffffff",
          hover: "#0a3d8f",
        },
      },
    },
    light: {
      background: {
        primary: "#ffffff",
        secondary: "#f8f9fa",
        tertiary: "#f1f3f4",
        card: "#ffffff",
        cardHover: "#f8f9fa",
        paper: "#ffffff",
        drawer: "#fafafa",
      },
      text: {
        primary: "#1a1a1a",
        secondary: "#4a4a4a",
        tertiary: "#666666",
        muted: "#888888",
      },
      border: {
        primary: "#e5e7eb",
        secondary: "#d1d5db",
        tertiary: "#9ca3af",
      },
      accent: {
        primary: "rgba(120, 119, 198, 0.08)",
        secondary: "rgba(120, 119, 198, 0.05)",
      },
      button: {
        outlined: {
          border: "#d1d5db",
          text: "#1a1a1a",
          hover: "#f1f3f4",
        },
        contained: {
          background: "#0F52BA",
          text: "#ffffff",
          hover: "#0a3d8f",
        },
      },
    },
  };

  const currentTheme = isDarkMode ? themeColors.dark : themeColors.light;

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#0F52BA",
        dark: "#0a3d8f",
        light: "#4a7bc8",
      },
      background: {
        default: currentTheme.background.primary,
        paper: currentTheme.background.paper,
      },
      text: {
        primary: currentTheme.text.primary,
        secondary: currentTheme.text.secondary,
      },
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: currentTheme.background.drawer,
            borderRight: `1px solid ${currentTheme.border.primary}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: currentTheme.background.paper,
            color: currentTheme.text.primary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          outlined: {
            borderColor: currentTheme.button.outlined.border,
            color: currentTheme.button.outlined.text,
            "&:hover": {
              backgroundColor: currentTheme.button.outlined.hover,
              borderColor: currentTheme.border.secondary,
            },
          },
          contained: {
            backgroundColor: currentTheme.button.contained.background,
            color: currentTheme.button.contained.text,
            "&:hover": {
              backgroundColor: currentTheme.button.contained.hover,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
            borderBottom: `1px solid ${currentTheme.border.primary}`,
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  });

  const getBackgroundGradient = () => {
    const baseColor = isDarkMode
      ? "rgba(120, 119, 198, 0.03)"
      : "rgba(120, 119, 198, 0.08)";
    const secondaryColor = isDarkMode
      ? "rgba(120, 119, 198, 0.02)"
      : "rgba(120, 119, 198, 0.05)";

    return `
      radial-gradient(circle at 20% 80%, ${baseColor} 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, ${baseColor} 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, ${secondaryColor} 0%, transparent 50%)
    `;
  };

  const value = {
    isDarkMode,
    toggleTheme,
    colors: currentTheme,
    getBackgroundGradient,

    getCardStyles: () => ({
      backgroundColor: currentTheme.background.card,
      border: `1px solid ${currentTheme.border.primary}`,
      color: currentTheme.text.primary,
      "&:hover": {
        backgroundColor: currentTheme.background.cardHover,
        borderColor: currentTheme.border.secondary,
      },
    }),
    getDrawerStyles: () => ({
      backgroundColor: currentTheme.background.drawer,
      borderRight: `1px solid ${currentTheme.border.primary}`,
      "& .MuiDrawer-paper": {
        backgroundColor: currentTheme.background.drawer,
        borderRight: `1px solid ${currentTheme.border.primary}`,
      },
    }),
  };

  return (
    <CustomThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CustomThemeContext.Provider>
  );
};
